/**
 * Central Authentication Module
 *
 * Handles OAuth 2.0 PKCE flow with Central for authentication.
 * This is the ONLY way to authenticate in Legacy - we never issue our own tokens.
 *
 * SUITE STANDARD:
 * - Uses /api/oidc/* endpoints (proxied through backend)
 * - Authorization Code Flow + PKCE (S256)
 * - RS256 tokens verified by backend via JWKS
 * - NO symmetric secrets - asymmetric verification only
 *
 * TOKEN STORAGE: In-memory only (no localStorage/sessionStorage for tokens)
 * - Refresh requires Central session
 * - Page reload requires re-login (until httpOnly cookie refresh exists)
 * - This is the secure default - XSS cannot steal tokens from memory
 *
 * PROXY: All Central calls go through the backend at /api/v1/central/*
 * so the Central URL is never exposed in the frontend bundle.
 */

import { generatePKCE, base64UrlEncode, PKCE_VERIFIER_KEY } from '@levelup/central-auth';

const API_BASE_URL = import.meta.env.VITE_API_URL;

/**
 * Auth error with structured context for display.
 * Production-safe - no secrets, only error codes and status.
 */
export class AuthError extends Error {
  public readonly errorCode: string;
  public readonly httpStatus?: number;

  constructor(message: string, errorCode: string, httpStatus?: number) {
    super(message);
    this.name = 'AuthError';
    this.errorCode = errorCode;
    this.httpStatus = httpStatus;
  }
}

// In-memory token storage (not persisted - secure by design)
let accessToken: string | null = null;
let refreshToken: string | null = null;

/**
 * Generates a cryptographically secure random string for use as CSRF state.
 */
function generateRandomState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Gets the redirect URI for the current environment.
 */
function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * Validates that the backend API URL is configured.
 * @throws AuthError if VITE_API_URL is not set
 */
function validateConfig(): void {
  if (!API_BASE_URL) {
    throw new AuthError(
      'Backend API URL not configured',
      'BACKEND_NOT_CONFIGURED'
    );
  }
}

// De-duplicate concurrent startLogin invocations (React StrictMode double-mount
// in dev would otherwise generate two PKCE pairs and overwrite the first
// verifier — causing the eventual token exchange to fail with invalid_grant
// when the code was issued against the first challenge).
let startLoginInFlight: Promise<void> | null = null;

/**
 * Initiates the login flow by requesting the authorize URL from the backend.
 *
 * Uses OAuth 2.0 Authorization Code flow with PKCE.
 * Backend builds the Central authorize URL and returns it.
 */
export async function startLogin(): Promise<void> {
  if (startLoginInFlight) return startLoginInFlight;
  startLoginInFlight = startLoginImpl();
  try {
    await startLoginInFlight;
  } finally {
    // The normal path is window.location.href = ... which unloads this JS
    // context entirely. The finally block runs only on error paths — reset
    // so a Retry click can attempt again.
    startLoginInFlight = null;
  }
}

async function startLoginImpl(): Promise<void> {
  validateConfig();

  // Generate PKCE code verifier and challenge (shared suite helper)
  const { codeVerifier, codeChallenge } = await generatePKCE();

  // Store code verifier temporarily for token exchange (sessionStorage OK for PKCE verifier)
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);

  // Ask backend to build the authorize URL
  const params = new URLSearchParams({
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state: generateRandomState(), // Random state for CSRF protection
    redirect_uri: getRedirectUri(),
    scope: 'openid profile email',
  });

  const response = await fetch(`${API_BASE_URL}/central/auth/login?${params.toString()}`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new AuthError(
      errorData.error || 'Failed to get login URL',
      'LOGIN_URL_FAILED',
      response.status
    );
  }

  const { authorizeUrl } = await response.json();

  // Top-level redirect (not popup)
  window.location.href = authorizeUrl;
}

/**
 * Handles the OAuth callback after Central redirects back.
 *
 * @param searchParams - URL search params from callback
 * @returns The access token if successful
 * @throws Error if callback fails
 */
export async function handleCallback(
  searchParams: URLSearchParams
): Promise<string> {
  validateConfig();

  // Check for error response from Central
  const error = searchParams.get('error');
  if (error) {
    const errorDescription = searchParams.get('error_description') || error;
    throw new AuthError(
      errorDescription,
      `CENTRAL_${error.toUpperCase().replace(/-/g, '_')}`
    );
  }

  // Get authorization code
  const code = searchParams.get('code');
  if (!code) {
    throw new AuthError(
      'No authorization code received from Central',
      'MISSING_AUTH_CODE'
    );
  }

  // Get stored code verifier
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (!codeVerifier) {
    throw new AuthError(
      'Session expired. Please try logging in again.',
      'MISSING_CODE_VERIFIER'
    );
  }

  // Exchange code for tokens via backend proxy
  const tokenResponse = await fetch(`${API_BASE_URL}/central/auth/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      code,
      redirect_uri: getRedirectUri(),
      code_verifier: codeVerifier,
    }),
  });

  // Clear code verifier
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  if (!tokenResponse.ok) {
    const errorData = await tokenResponse.json().catch(() => ({}));
    const errorCode = errorData.error
      ? `TOKEN_${errorData.error.toUpperCase().replace(/-/g, '_')}`
      : 'TOKEN_EXCHANGE_FAILED';
    throw new AuthError(
      errorData.error_description || errorData.error || 'Token exchange failed',
      errorCode,
      tokenResponse.status
    );
  }

  const tokens = await tokenResponse.json();

  // Store tokens IN MEMORY ONLY (secure - not accessible via XSS)
  if (tokens.access_token) {
    accessToken = tokens.access_token;
  }
  if (tokens.refresh_token) {
    refreshToken = tokens.refresh_token;
  }

  return tokens.access_token;
}

/**
 * Refreshes the access token using the refresh token.
 *
 * @returns New access token if successful, null if refresh fails
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshToken || !API_BASE_URL) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/central/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      // Refresh failed - clear tokens and require re-login
      logout();
      return null;
    }

    const tokens = await response.json();

    // Update in-memory tokens
    if (tokens.access_token) {
      accessToken = tokens.access_token;
    }
    if (tokens.refresh_token) {
      refreshToken = tokens.refresh_token;
    }

    return tokens.access_token;
  } catch {
    // Refresh failed - caller handles null return
    return null;
  }
}

/**
 * Logs out the user by clearing tokens and optionally redirecting via backend logout URL.
 *
 * @param redirectToCentral - Whether to redirect to Central's logout endpoint
 */
export async function logout(redirectToCentral = false): Promise<void> {
  // Clear in-memory tokens
  accessToken = null;
  refreshToken = null;

  // Clear PKCE verifier if present
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  if (redirectToCentral && API_BASE_URL) {
    const postLogoutUri = window.location.origin;
    const params = new URLSearchParams({
      post_logout_redirect_uri: postLogoutUri,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/central/auth/logout-url?${params.toString()}`);
      if (response.ok) {
        const { logoutUrl } = await response.json();
        window.location.href = logoutUrl;
      }
    } catch {
      // If backend is unreachable, just redirect to home
      window.location.href = postLogoutUri;
    }
  }
}

/**
 * Gets the current access token from memory.
 *
 * NOTE: Returns null after page reload. User must re-authenticate.
 * This is intentional - in-memory storage prevents XSS token theft.
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Sets the access token (used by auth hook after callback).
 * Only for internal use during token exchange.
 */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Checks if user has a token in memory (may be expired).
 */
export function hasToken(): boolean {
  return accessToken !== null;
}

/**
 * Checks if the backend API URL is configured.
 */
export function isBackendConfigured(): boolean {
  return !!API_BASE_URL;
}

// Session storage key for workspace switch state
const WORKSPACE_SWITCH_KEY = 'legacy_workspace_switch';

/**
 * Initiates a workspace switch by starting the OIDC authorize flow.
 *
 * SUITE STANDARD:
 * 1. Caller must have already called Central's select-workspace endpoint
 * 2. This triggers OIDC authorize redirect
 * 3. On callback, new token will have updated workspaceId
 *
 * @param targetWorkspaceId - The workspace being switched to (for state tracking)
 */
export async function startWorkspaceSwitch(targetWorkspaceId: string): Promise<void> {
  validateConfig();

  // Generate PKCE code verifier and challenge (shared suite helper)
  const { codeVerifier, codeChallenge } = await generatePKCE();

  // Store code verifier for token exchange
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);

  // Store workspace switch state so callback knows this was a switch
  sessionStorage.setItem(WORKSPACE_SWITCH_KEY, targetWorkspaceId);

  // Ask backend to build the authorize URL
  const params = new URLSearchParams({
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state: generateRandomState(), // Random state for CSRF protection
    redirect_uri: getRedirectUri(),
    scope: 'openid profile email',
  });

  const response = await fetch(`${API_BASE_URL}/central/auth/login?${params.toString()}`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new AuthError(
      errorData.error || 'Failed to get login URL',
      'LOGIN_URL_FAILED',
      response.status
    );
  }

  const { authorizeUrl } = await response.json();

  // Top-level redirect (Central will use the session's selected workspace)
  window.location.href = authorizeUrl;
}

/**
 * Checks if the current callback is from a workspace switch.
 * @returns The target workspace ID if this was a switch, null otherwise
 */
export function getWorkspaceSwitchTarget(): string | null {
  return sessionStorage.getItem(WORKSPACE_SWITCH_KEY);
}

/**
 * Clears the workspace switch state after handling callback.
 */
export function clearWorkspaceSwitchState(): void {
  sessionStorage.removeItem(WORKSPACE_SWITCH_KEY);
}
